import { inject } from '@angular/core';
import { CanActivateFn, CanActivateChildFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';

export interface PermissionRouteData {
    permissions?: string[];
    permissionMode?: 'any' | 'all';
    redirectTo?: string;
}

/**
 * Guard that checks if user has required permissions.
 * Configure via route data:
 * 
 * {
 *   path: 'products',
 *   canActivate: [PermissionGuard],
 *   data: { 
 *     permissions: ['product:read'], 
 *     permissionMode: 'any',  // 'any' (default) or 'all'
 *     redirectTo: '/dashboard'  // optional redirect on failure
 *   }
 * }
 */
export const PermissionGuard: CanActivateFn | CanActivateChildFn = (route: ActivatedRouteSnapshot) => {
    const authService = inject(AuthService);
    const router = inject(Router);

    const data = route.data as PermissionRouteData;
    const requiredPermissions = data.permissions || [];
    const mode = data.permissionMode || 'any';
    const redirectTo = data.redirectTo || '/access-denied';

    // No permissions required, allow access
    if (requiredPermissions.length === 0) {
        return true;
    }

    // Check permissions
    const hasAccess = mode === 'any'
        ? authService.hasAnyPermission(requiredPermissions)
        : authService.hasAllPermissions(requiredPermissions);

    if (!hasAccess) {
        // console.warn(`[PermissionGuard] Access denied. Required: ${requiredPermissions.join(', ')} (mode: ${mode})`);
        return router.parseUrl(redirectTo);
    }

    return true;
};

/**
 * Factory function for creating permission guards with specific permissions
 * Usage: canActivate: [requirePermission('product:create')]
 */
export function requirePermission(...permissions: string[]): CanActivateFn {
    return (route) => {
        const authService = inject(AuthService);
        const router = inject(Router);

        const hasAccess = authService.hasAnyPermission(permissions);

        if (!hasAccess) {
            // console.warn(`[requirePermission] Access denied. Required: ${permissions.join(', ')}`);
            return router.parseUrl('/access-denied');
        }

        return true;
    };
}

/**
 * Factory function requiring ALL specified permissions
 */
export function requireAllPermissions(...permissions: string[]): CanActivateFn {
    return (route) => {
        const authService = inject(AuthService);
        const router = inject(Router);

        const hasAccess = authService.hasAllPermissions(permissions);

        if (!hasAccess) {
            // console.warn(`[requireAllPermissions] Access denied. Required all: ${permissions.join(', ')}`);
            return router.parseUrl('/access-denied');
        }

        return true;
    };
}
